import { HHTextField } from '@hinge-health/react-component-library';
import { InputAdornment } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { BillingModelType } from '../../../../types';
import {
  fieldHasErrors,
  getFormErrorsForContractField,
  isAdminFeeClaimsModelById,
  isFeeForServiceModelById,
} from '../../../../utils/contract-utils';
import { getCurrencySymbol } from '../../../../utils/currency-helpers';
import { RawInnerPackageFormValues } from '../custom-package-types';
import { ContractPackageFormLabelText } from '../form';

export interface UserAnnualCapProps {
  wholeFormDisabled: boolean | undefined;
  billingModels: BillingModelType[];
}
export const USER_ANNUAL_CAP_TEST_ID = 'user-annual-cap-test-id';

export const UserAnnualCap = ({
  wholeFormDisabled,
  billingModels,
}: UserAnnualCapProps): JSX.Element => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const [watchedCurrency, watchedSelectedPackages] = useWatch({
    name: ['currency', 'selectedPackages'],
  });

  const capEligiblePackages = watchedSelectedPackages.filter(
    (pkg: RawInnerPackageFormValues) =>
      !isAdminFeeClaimsModelById(billingModels, pkg.billingModelId) &&
      !isFeeForServiceModelById(billingModels, pkg.billingModelId),
  );
  const maxPrice = capEligiblePackages.length
    ? Math.max(
        ...capEligiblePackages.map(
          (pkg: RawInnerPackageFormValues) => pkg.price,
        ),
      )
    : 0;

  useEffect(() => {
    if (wholeFormDisabled) return;
    setValue('userAnnualCap', maxPrice, { shouldValidate: true });
  }, [maxPrice, setValue, wholeFormDisabled]);

  return (
    <Controller
      control={control}
      name="userAnnualCap"
      render={({ field: { onChange, value } }): JSX.Element => (
        <HHTextField
          data-testid={USER_ANNUAL_CAP_TEST_ID}
          hhVariant="variant-bypass"
          label={ContractPackageFormLabelText.USER_ANNUAL_CAP}
          key={value.id}
          fullWidth
          disabled={wholeFormDisabled}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*',
            step: '0.01',
            min: '0',
            name: 'userAnnualCap',
            role: 'textbox',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                disableTypography
                sx={{ color: 'gray' }}
              >
                {getCurrencySymbol(watchedCurrency?.value)}
              </InputAdornment>
            ),
          }}
          error={fieldHasErrors('userAnnualCap', errors)}
          helperText={getFormErrorsForContractField('userAnnualCap', errors)}
          value={value}
          onChange={onChange}
          type="number"
          InputLabelProps={{ sx: { color: 'gray' } }}
        />
      )}
    />
  );
};
